import { useState, useRef, useEffect } from 'react'
import { useApp } from '@/store/AppContext'
import { useAuth } from '@/hooks/use-auth'
import { formatPlate } from '@/lib/pricing'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Car, Bike, Package, CheckCircle2, AlertCircle } from 'lucide-react'
import { VehicleType } from '@/lib/types'

interface CheckInFormProps {
  initialSpaceId?: string
  onSuccess?: () => void
  isDialog?: boolean
}

export function CheckInForm({
  initialSpaceId,
  onSuccess,
  isDialog,
}: CheckInFormProps) {
  const [plate, setPlate] = useState('')
  const [type, setType] = useState<VehicleType>('Carro')
  const [notes, setNotes] = useState('')
  const [spaceId, setSpaceId] = useState<string>(initialSpaceId || 'none')

  const { checkIn, tickets, spaces, isOperationMode, subscribers } = useApp()
  const { profile } = useAuth()
  const { toast } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !isDialog &&
        e.key === '/' &&
        document.activeElement !== inputRef.current &&
        document.activeElement?.tagName !== 'INPUT'
      ) {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDialog])

  const matchingSub = subscribers.find((s) => s.plates.includes(plate))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (plate.length < 7) {
      toast({
        title: 'Erro',
        description: 'Placa inválida.',
        variant: 'destructive',
      })
      return
    }
    const isActive = tickets.some(
      (t) => t.plate === plate && t.status === 'active',
    )
    if (isActive) {
      toast({
        title: 'Atenção',
        description: 'Veículo já está no pátio.',
        variant: 'destructive',
      })
      return
    }

    const isSub = !!matchingSub
    const isMonthly = isSub || notes.toLowerCase().includes('mensalista')
    const finalType = isSub ? matchingSub.vehicleType : type
    const finalNotes = isSub
      ? `Mensalista: ${matchingSub.name}${notes ? ` - ${notes}` : ''}`
      : notes

    checkIn({
      plate,
      type: finalType,
      notes: finalNotes,
      spaceId: spaceId !== 'none' ? spaceId : undefined,
      isMonthly,
      operatorName: profile?.name || 'Sistema',
    })

    toast({ title: 'Sucesso', description: 'Entrada registrada com sucesso!' })
    setPlate('')
    setNotes('')
    setSpaceId('none')
    inputRef.current?.focus()
    if (onSuccess) onSuccess()
  }

  const isValidPlate = plate.length >= 7
  const availableSpaces = spaces.filter(
    (s) => s.status === 'available' || s.id === initialSpaceId,
  )

  return (
    <Card
      className={`border-none shadow-sm h-fit ${!isDialog ? 'md:border-solid md:shadow-elevation' : 'shadow-none bg-background'}`}
    >
      <CardHeader
        className={`pb-4 flex flex-row items-center justify-between ${isDialog ? 'px-0 pt-0' : ''}`}
      >
        <CardTitle
          className={isOperationMode ? 'text-2xl' : 'text-xl md:text-2xl'}
        >
          Registrar Entrada
        </CardTitle>
        {!isDialog && (
          <div className="hidden md:flex text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
            Atalho: <kbd className="font-mono ml-1 font-bold">/</kbd>
          </div>
        )}
      </CardHeader>
      <CardContent className={`${isDialog ? 'px-0 pb-0' : ''}`}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="plate" className="text-base">
                Placa do Veículo
              </Label>
              {isValidPlate && !matchingSub && (
                <span className="text-xs text-emerald-600 flex items-center font-medium animate-fade-in">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Placa válida
                </span>
              )}
            </div>
            <Input
              id="plate"
              ref={inputRef}
              value={plate}
              onChange={(e) => setPlate(formatPlate(e.target.value))}
              placeholder="ABC-1234"
              className={`${isOperationMode ? 'h-20 text-3xl' : 'input-mobile-large text-xl'} text-center font-black tracking-widest uppercase bg-muted/30 focus:bg-background transition-colors`}
              autoComplete="off"
            />
            {matchingSub && (
              <div
                className={`mt-3 p-3 rounded-lg border flex flex-col gap-1 shadow-sm animate-fade-in-up ${
                  matchingSub.status === 'Ativo'
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : matchingSub.status === 'Inadimplente'
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  {matchingSub.status === 'Ativo' ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <AlertCircle className="h-5 w-5" />
                  )}
                  <span className="font-bold">
                    {matchingSub.status === 'Ativo'
                      ? '🟢 Mensalista Ativo'
                      : matchingSub.status === 'Inadimplente'
                        ? `⚠️ Mensalidade vencida (Dia ${matchingSub.dueDate})`
                        : 'Mensalista Suspenso'}
                  </span>
                </div>
                <div className="text-xs pl-7 opacity-90">
                  {matchingSub.name} • {matchingSub.vehicleType}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-base">Tipo de Veículo</Label>
            <ToggleGroup
              type="single"
              value={matchingSub ? matchingSub.vehicleType : type}
              onValueChange={(v) => v && setType(v as VehicleType)}
              disabled={!!matchingSub}
              className="w-full justify-start gap-2"
            >
              <ToggleGroupItem
                value="Carro"
                aria-label="Carro"
                className={`flex-1 ${isOperationMode ? 'h-16 text-lg' : 'h-12'} data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border`}
              >
                <Car className="h-5 w-5 mr-2" /> Carro
              </ToggleGroupItem>
              <ToggleGroupItem
                value="Moto"
                aria-label="Moto"
                className={`flex-1 ${isOperationMode ? 'h-16 text-lg' : 'h-12'} data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border`}
              >
                <Bike className="h-5 w-5 mr-2" /> Moto
              </ToggleGroupItem>
              <ToggleGroupItem
                value="Outros"
                aria-label="Outros"
                className={`flex-1 ${isOperationMode ? 'h-16 text-lg' : 'h-12'} data-[state=on]:bg-primary data-[state=on]:text-primary-foreground border`}
              >
                <Package className="h-5 w-5 mr-2" /> Outros
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="space" className="text-base">
              Vaga (Opcional)
            </Label>
            <Select value={spaceId} onValueChange={setSpaceId}>
              <SelectTrigger
                className={`bg-background ${isOperationMode ? 'h-14 text-lg' : 'h-12'}`}
              >
                <SelectValue placeholder="Selecione uma vaga" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma / Vaga Livre</SelectItem>
                {availableSpaces.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    Vaga {s.number} ({s.type.toUpperCase()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base">
              Observações (Opcional)
            </Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Amassado..."
              className={isOperationMode ? 'h-14' : 'h-12'}
            />
          </div>

          <Button
            type="submit"
            className={`w-full font-bold shadow-md ${isOperationMode ? 'h-16 text-xl' : 'btn-mobile-large mt-4'}`}
          >
            Registrar Entrada{' '}
            <kbd className="hidden md:inline-block ml-2 text-xs font-normal opacity-70 border border-current rounded px-1">
              Enter
            </kbd>
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
